/**
 * Mobile / Capacitor integration shim.
 *
 * Today this file just does pure-web mobile polish (Android back button,
 * safe-area CSS variables, install banner). When you're ready to wrap the
 * app in Capacitor, import the optional plugins below.
 *
 * No-op on desktop. No external deps required for the web build.
 */

let _backButtonHandler = null;
let _linkInterceptorHandler = null;

/**
 * App Store Compliance — Apple Guideline 4 / 4.8.
 *
 * On native iOS (WKWebView) and Android (Chrome WebView), any anchor with
 * `target="_blank"` or `rel="noopener"` opens the SYSTEM default browser
 * (external Safari / Chrome) — which Apple Review treats as "leaving the app
 * for a poor user experience".
 *
 * This installs a single document-level click interceptor that runs only on
 * native. It catches every anchor tap before navigation and:
 *
 *   • Same-origin links (e.g. /privacy, /terms, /reset-password) →
 *     handle via the History API + popstate so React Router catches them.
 *     Never opens any browser.
 *
 *   • External links (e.g. https://example.com, mailto:, tel:) →
 *     open inside the app via @capacitor/browser (SFSafariViewController on
 *     iOS, Chrome Custom Tab on Android). The user stays in the app context.
 *
 *   • mailto:/tel:/sms: → delegated to the OS, which is expected.
 */
function installNativeLinkInterceptor(navigateFn) {
  const isNative = !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
  if (!isNative) return null;
  if (_linkInterceptorHandler) return _linkInterceptorHandler;

  const handler = async (e) => {
    // Walk up to nearest <a>.
    let el = e.target;
    while (el && el.nodeName !== 'A') el = el.parentElement;
    if (!el) return;
    const href = el.getAttribute('href');
    if (!href) return;
    // Skip pure anchors, JS hooks, framework router clicks, and OS schemes.
    if (href.startsWith('#') || href.startsWith('javascript:')) return;
    if (href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('sms:')) return;
    // Skip if a router has already handled it via onClick (preventDefault was called).
    if (e.defaultPrevented) return;

    let url;
    try {
      url = new URL(href, window.location.href);
    } catch {
      return;
    }

    const isSameOrigin = url.origin === window.location.origin
      || url.protocol === 'capacitor:' || url.host === 'localhost';

    if (isSameOrigin) {
      // In-app SPA navigation. Cancel the default (which would full-page-load
      // and may escape on target=_blank) and push state instead.
      e.preventDefault();
      const path = url.pathname + url.search + url.hash;
      if (navigateFn) {
        navigateFn(path);
      } else {
        window.history.pushState({}, '', path);
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
      return;
    }

    // External http(s) — open in-app via SFSafariViewController.
    e.preventDefault();
    try {
      const { Browser } = await import('@capacitor/browser');
      await Browser.open({ url: url.toString(), presentationStyle: 'popover' });
    } catch {
      // Plugin not installed in this build — fall back to in-WebView nav, which
      // is still in-app (no external Safari), just not chrome-styled.
      window.location.href = url.toString();
    }
  };

  document.addEventListener('click', handler, true);
  _linkInterceptorHandler = handler;
  return handler;
}

/**
 * Initialize mobile-environment behaviors that work both in plain mobile
 * browsers and inside a Capacitor WebView. Call once at app boot.
 */
export function initMobileEnvironment(navigateFn) {
  if (typeof window === 'undefined') return;

  // ── Android hardware back button (browser via popstate, Capacitor via App plugin) ──
  // Pure-web fallback: pushing a state lets the user "back-out" of modals before leaving.
  if (!window.__cyboardBackInit) {
    window.__cyboardBackInit = true;
    // Mark that the user has interacted; lets us push state safely on first tap.
    window.history.replaceState({ cyboard: true }, '');
  }

  // ── App Store compliance: keep all link taps inside the app ──
  installNativeLinkInterceptor(navigateFn);

  // ── Location auto-refresh on app resume ──
  try {
    import('./locationService')
      .then((mod) => mod.installLocationAutoRefresh && mod.installLocationAutoRefresh())
      .catch(() => {});
  } catch { /* ignore */ }

  // ── Capacitor App plugin hook (no-op if Capacitor isn't bundled) ──
  // We dynamically check window.Capacitor so the web build doesn't try to import.
  try {
    if (window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.App) {
      const App = window.Capacitor.Plugins.App;
      if (_backButtonHandler) {
        // Already registered — no-op
      } else {
        // addListener returns a Promise<PluginListenerHandle>; resolve it before
        // storing so teardown can call .remove() safely.
        const p = App.addListener('backButton', ({ canGoBack }) => {
          if (canGoBack) {
            window.history.back();
          } else if (navigateFn) {
            navigateFn('/');
          }
        });
        if (p && typeof p.then === 'function') {
          p.then((handle) => { _backButtonHandler = handle; }).catch(() => { /* ignore */ });
        } else {
          _backButtonHandler = p;
        }
      }
    }
  } catch {
    /* ignore */
  }

  // ── CSS safe-area variables (iPhone notch + Android cutouts) ──
  // index.html already has viewport-fit=cover; expose a utility class for layouts.
  const styleId = 'cyboard-safe-area-style';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    const isNative = !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
    // On native we hard-floor the top inset to 32px (status bar ~24px + ~8px
    // breathing room) because Android edge-to-edge enforcement makes WebView
    // ignore overlaysWebView and `env(safe-area-inset-top)` often returns 0
    // on phones without a notch.
    const safeTop = isNative ? 'max(env(safe-area-inset-top, 0px), 32px)' : 'env(safe-area-inset-top, 0px)';
    const safeBottom = isNative ? 'max(env(safe-area-inset-bottom, 0px), 16px)' : 'env(safe-area-inset-bottom, 0px)';
    style.textContent = `
      :root {
        --cyboard-safe-top: ${safeTop};
        --cyboard-safe-bottom: ${safeBottom};
        --cyboard-safe-left: env(safe-area-inset-left, 0px);
        --cyboard-safe-right: env(safe-area-inset-right, 0px);
      }
      /* Push every page below the status bar / Dynamic Island on native.
         Modal overlays, login splash, etc. are absolutely-positioned, so they
         get their own padding; this rule covers all in-flow content. */
      ${isNative ? 'body { padding-top: var(--cyboard-safe-top); }' : ''}
      .safe-area-pad-top, .safe-area-top { padding-top: var(--cyboard-safe-top); }
      .safe-area-pad-bottom, .safe-area-bottom { padding-bottom: var(--cyboard-safe-bottom); }
      .safe-area-pad-x { padding-left: var(--cyboard-safe-left); padding-right: var(--cyboard-safe-right); }
      /* Disable iOS bounce/elastic scroll on the body */
      html, body { overscroll-behavior-y: none; }
      /* Disable text selection callout on long-press for buttons */
      button { -webkit-touch-callout: none; -webkit-user-select: none; user-select: none; }
    `;
    document.head.appendChild(style);
  }
}

/**
 * Detect whether we're running inside a native wrapper.
 */
export function isNativeMobile() {
  return !!(typeof window !== 'undefined' && window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
}

/**
 * Detect a touch-primary device (rough heuristic).
 */
export function isTouchDevice() {
  if (typeof window === 'undefined') return false;
  return ('ontouchstart' in window) || (navigator.maxTouchPoints || 0) > 0;
}

/**
 * Cleanup (optional — for tests).
 */
export function teardownMobileEnvironment() {
  try {
    if (_backButtonHandler && typeof _backButtonHandler.remove === 'function') {
      _backButtonHandler.remove();
    }
  } catch { /* ignore */ }
  _backButtonHandler = null;
  if (_linkInterceptorHandler) {
    try { document.removeEventListener('click', _linkInterceptorHandler, true); } catch { /* ignore */ }
    _linkInterceptorHandler = null;
  }
}
